import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import TradePanel from "./components/TradePanel"; 
import SYMBOL_LIST, { SYMBOL_LOT_SIZES, SYMBOL_STRIKE_INCREMENT, SYMBOL_CONFIG_KEY_MAP } from './constants'; 
import AlgoDashboard from "./components/AlgoDashboard";

// --- CONFIG ---
const API_KEY = ""; // Keep empty or use your key
const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash-latest:generateContent?key=${API_KEY}`;
//const BACKEND_URL = 'http://localhost:5000';
//const WS_URL = 'ws://localhost:8080';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = process.env.REACT_APP_WS_URL;

// --- HELPER TO RESOLVE SYMBOL KEY ---
const getSymbolKey = (symbol) => {
    if (!symbol) return null;
    if (SYMBOL_CONFIG_KEY_MAP[symbol]) return SYMBOL_CONFIG_KEY_MAP[symbol];
    // Handle NSE:RELIANCE-EQ or NSE:TCS logic
    if (symbol.includes(':')) {
        return symbol.split(':')[1].replace('-EQ', '');
    }
    return symbol;
};

// --- LIVE CANDLE CHART COMPONENT ---
const LiveCandleChart = ({ data }) => {
    if (!data || data.length === 0) return (
        <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded border border-dashed border-gray-300 dark:border-gray-600">
            <span className="text-gray-400">Waiting for Live Data Stream...</span>
        </div>
    );
    
    return (
        <div className="h-72 w-full bg-white dark:bg-gray-800 rounded p-4 shadow border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold text-indigo-600 dark:text-indigo-400">Live Market Feed (1-Min Candles)</h3>
                <span className="text-xs text-gray-500">Live WebSocket</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                    <XAxis 
                        dataKey="time" 
                        tickFormatter={(tick) => new Date(tick * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} 
                        tick={{fontSize: 10}}
                    />
                    <YAxis domain={['auto', 'auto']} tick={{fontSize: 10}} width={40} />
                    <Tooltip 
                        labelFormatter={(label) => new Date(label * 1000).toLocaleTimeString()}
                        contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#fff', fontSize: '12px' }}
                    />
                    <Line type="monotone" dataKey="close" stroke="#8884d8" dot={false} strokeWidth={2} isAnimationActive={false} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

// --- STRATEGY DEFINITIONS (Matches strategyCalculator.js) ---
const strategyGroups = [
    {
        label: "Bullish Strategies",
        options: [
            { value: 'long-call', name: 'Long Call', fields: ['strike', 'premium', 'lots', 'lotSize'] },
            { value: 'bull-call-spread', name: 'Bull Call Spread', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'lots', 'lotSize'] },
            { value: 'bull-put-spread', name: 'Bull Put Spread', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'lots', 'lotSize'] },
            { value: 'call-ratio-spread', name: 'Call Ratio Spread', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'lots', 'lotSize'] },
            { value: 'jade-lizard', name: 'Jade Lizard', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'strike3', 'premium3', 'lots', 'lotSize'] }, 
            { value: 'synthetic-long-stock', name: 'Synthetic Long', fields: ['strike', 'premium', 'premium2', 'lots', 'lotSize'] },
            { value: 'protective-call', name: 'Covered Call', fields: ['stockPrice', 'strike', 'premium', 'lots', 'lotSize'] },
        ]
    },
    {
        label: "Bearish Strategies",
        options: [
            { value: 'long-put', name: 'Long Put', fields: ['strike', 'premium', 'lots', 'lotSize'] },
            { value: 'short-call', name: 'Short Call', fields: ['strike', 'premium', 'lots', 'lotSize'] }, 
            { value: 'bear-put-spread', name: 'Bear Put Spread', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'lots', 'lotSize'] },
            { value: 'bear-call-spread', name: 'Bear Call Spread', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'lots', 'lotSize'] },
            { value: 'synthetic-short-stock', name: 'Synthetic Short', fields: ['strike', 'premium', 'premium2', 'lots', 'lotSize'] },
            { value: 'protective-put', name: 'Protective Put', fields: ['stockPrice', 'strike', 'premium', 'lots', 'lotSize'] },
        ]
    },
    {
        label: "Neutral / Volatility",
        options: [
            { value: 'short-straddle', name: 'Short Straddle', fields: ['strike', 'premium1', 'premium2', 'lots', 'lotSize'] },
            { value: 'long-straddle', name: 'Long Straddle', fields: ['strike', 'premium1', 'premium2', 'lots', 'lotSize'] },
            { value: 'short-strangle', name: 'Short Strangle', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'lots', 'lotSize'] },
            { value: 'long-strangle', name: 'Long Strangle', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'lots', 'lotSize'] },
            { value: 'iron-condor', name: 'Iron Condor', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'strike3', 'premium3', 'strike4', 'premium4', 'lots', 'lotSize'] },
            { value: 'iron-butterfly', name: 'Iron Butterfly', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'premium3', 'strike3', 'premium4', 'lots', 'lotSize'] },
            { value: 'call-butterfly', name: 'Call Butterfly', fields: ['strike1', 'premium1', 'strike2', 'premium2', 'strike3', 'premium3', 'lots', 'lotSize'] }, 
        ]
    }
];

const strategyConfigs = strategyGroups.flatMap(group => group.options).reduce((acc, option) => {
    acc[option.value] = { name: option.name, fields: option.fields };
    return acc;
}, {});

// --- HELPER FUNCTIONS ---
const formatLabel = (fieldName, strategy) => {
    if (strategy === 'calendar-spread') {
        if (fieldName === 'premium1') return 'Long-Term Premium';
        if (fieldName === 'premium2') return 'Short-Term Premium';
    }
    const labels = {
        lotSize: 'Lot Size', stockPrice: 'Underlying Price', premium: 'Premium',
        premium1: 'Premium 1', premium2: 'Premium 2', premium3: 'Premium 3', premium4: 'Premium 4',
        strike: 'Strike', strike1: 'Strike 1', strike2: 'Strike 2', strike3: 'Strike 3', strike4: 'Strike 4',
        targetPercent: 'Target %', slPercent: 'Stop-Loss %'
    };
    return labels[fieldName] || fieldName.replace(/(\d+)/, ' $1').replace(/^\w/, c => c.toUpperCase());
};

const translateFormToTrade = (strategy, form, currentSymbol) => {
    const lots = form.lots || 1;
    let tradeLegs = [];
    const symbol = currentSymbol; 
    let strategyType = strategyConfigs[strategy].name;

    try {
        switch (strategy) {
            case 'long-call': tradeLegs.push({ strike: form.strike, optionType: 'CE', action: 'BUY', qty: lots }); break;
            case 'short-call': tradeLegs.push({ strike: form.strike, optionType: 'CE', action: 'SELL', qty: lots }); break;
            case 'long-put': tradeLegs.push({ strike: form.strike, optionType: 'PE', action: 'BUY', qty: lots }); break;
            case 'short-put': tradeLegs.push({ strike: form.strike, optionType: 'PE', action: 'SELL', qty: lots }); break;
            case 'bull-call-spread':
                tradeLegs.push({ strike: form.strike1, optionType: 'CE', action: 'BUY', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'CE', action: 'SELL', qty: lots }); break;
            case 'bull-put-spread':
                tradeLegs.push({ strike: form.strike1, optionType: 'PE', action: 'SELL', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'PE', action: 'BUY', qty: lots }); break;
            case 'bear-call-spread':
                tradeLegs.push({ strike: form.strike1, optionType: 'CE', action: 'SELL', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'CE', action: 'BUY', qty: lots }); break;
            case 'bear-put-spread':
                tradeLegs.push({ strike: form.strike1, optionType: 'PE', action: 'BUY', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'PE', action: 'SELL', qty: lots }); break;
            case 'call-ratio-spread':
                tradeLegs.push({ strike: form.strike1, optionType: 'CE', action: 'BUY', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'CE', action: 'SELL', qty: lots * 2 }); break;
            case 'jade-lizard':
                tradeLegs.push({ strike: form.strike1, optionType: 'PE', action: 'SELL', qty: lots }); 
                tradeLegs.push({ strike: form.strike2, optionType: 'CE', action: 'SELL', qty: lots }); 
                tradeLegs.push({ strike: form.strike3, optionType: 'CE', action: 'BUY', qty: lots }); 
                break;
            case 'long-straddle':
            case 'short-straddle': 
                const actionStraddle = strategy === 'long-straddle' ? 'BUY' : 'SELL';
                tradeLegs.push({ strike: form.strike, optionType: 'CE', action: actionStraddle, qty: lots });
                tradeLegs.push({ strike: form.strike, optionType: 'PE', action: actionStraddle, qty: lots }); 
                break;
            case 'short-strangle':
                tradeLegs.push({ strike: form.strike1, optionType: 'PE', action: 'SELL', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'CE', action: 'SELL', qty: lots }); break;
            case 'long-strangle':
                tradeLegs.push({ strike: form.strike1, optionType: 'PE', action: 'BUY', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'CE', action: 'BUY', qty: lots }); break;
            case 'iron-condor':
                tradeLegs.push({ strike: form.strike1, optionType: 'PE', action: 'BUY', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'PE', action: 'SELL', qty: lots });
                tradeLegs.push({ strike: form.strike3, optionType: 'CE', action: 'SELL', qty: lots });
                tradeLegs.push({ strike: form.strike4, optionType: 'CE', action: 'BUY', qty: lots }); break;
            case 'iron-butterfly':
                tradeLegs.push({ strike: form.strike1, optionType: 'PE', action: 'BUY', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'PE', action: 'SELL', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'CE', action: 'SELL', qty: lots }); 
                tradeLegs.push({ strike: form.strike3, optionType: 'CE', action: 'BUY', qty: lots }); break;
            case 'call-butterfly':
                tradeLegs.push({ strike: form.strike1, optionType: 'CE', action: 'BUY', qty: lots });
                tradeLegs.push({ strike: form.strike2, optionType: 'CE', action: 'SELL', qty: lots * 2 });
                tradeLegs.push({ strike: form.strike3, optionType: 'CE', action: 'BUY', qty: lots }); break;
            case 'synthetic-long-stock':
                tradeLegs.push({ strike: form.strike, optionType: 'CE', action: 'BUY', qty: lots });
                tradeLegs.push({ strike: form.strike, optionType: 'PE', action: 'SELL', qty: lots }); break;
            case 'synthetic-short-stock':
                tradeLegs.push({ strike: form.strike, optionType: 'CE', action: 'SELL', qty: lots });
                tradeLegs.push({ strike: form.strike, optionType: 'PE', action: 'BUY', qty: lots }); break;
            default: return null;
        }
    } catch (error) {
        return null;
    }
    if (tradeLegs.length === 0) return null;

    return {
        symbol: symbol.toUpperCase(),
        strategyType: strategyType,
        legs: tradeLegs,
        targetPercent: form.targetPercent || 20,
        slPercent: form.slPercent || -10
    };
};

// --- FIX: Dynamic ATM finding for stocks ---
const findAtmStrike = (spot, symbolKey) => {
    const resolvedKey = getSymbolKey(symbolKey);
    let increment = 50; // Default for Nifty

    // Try to get explicit increment from constants
    if (resolvedKey && SYMBOL_STRIKE_INCREMENT[resolvedKey]) {
        increment = SYMBOL_STRIKE_INCREMENT[resolvedKey];
    } else {
        // Fallback for Stocks not in config: Estimate increment based on price
        if (spot < 500) increment = 5;
        else if (spot < 1500) increment = 10;
        else if (spot < 5000) increment = 20;
        else increment = 50;
    }
    
    return Math.round(spot / increment) * increment;
};

const autoFillPrimaryStrikes = (currentForm, currentStrategy, atmStrike, symbolKey) => {
    const newForm = { ...currentForm };
    const resolvedKey = getSymbolKey(symbolKey);
    // Reuse logic from findAtmStrike for increment consistency
    let increment = 50;
    if (resolvedKey && SYMBOL_STRIKE_INCREMENT[resolvedKey]) {
        increment = SYMBOL_STRIKE_INCREMENT[resolvedKey];
    } else {
         if (atmStrike < 500) increment = 5;
         else if (atmStrike < 1500) increment = 10;
         else if (atmStrike < 5000) increment = 20;
    }

    switch (currentStrategy) {
        case 'long-call': case 'long-put': case 'short-call': case 'short-put': 
        case 'long-straddle': case 'short-straddle': 
        case 'synthetic-long-stock': case 'synthetic-short-stock': 
        case 'calendar-spread':
            newForm.strike = atmStrike; break;
        case 'bull-call-spread': case 'bear-call-spread': case 'call-ratio-spread':
            newForm.strike1 = atmStrike; newForm.strike2 = atmStrike + increment; break;
        case 'bull-put-spread': case 'bear-put-spread':
            newForm.strike1 = atmStrike; newForm.strike2 = atmStrike - increment; break;
        case 'long-strangle': case 'short-strangle':
            newForm.strike1 = atmStrike - increment; newForm.strike2 = atmStrike + increment; break;
        case 'call-butterfly': case 'iron-butterfly':
            newForm.strike1 = atmStrike - increment; 
            newForm.strike2 = atmStrike;            
            newForm.strike3 = atmStrike + increment; 
            break;
        case 'jade-lizard':
            newForm.strike1 = atmStrike - increment;       
            newForm.strike2 = atmStrike + increment;       
            newForm.strike3 = atmStrike + (2 * increment); 
            break;
        case 'iron-condor':
            newForm.strike1 = atmStrike - (2 * increment); 
            newForm.strike2 = atmStrike - increment;
            newForm.strike3 = atmStrike + increment; 
            newForm.strike4 = atmStrike + (2 * increment); 
            break;
        case 'protective-put': case 'protective-call':
            newForm.stockPrice = atmStrike; newForm.strike = atmStrike; break;
        default: break;
    }
    return newForm;
};

// --- MAIN COMPONENT ---
function App() {

    const defaultFormState = {
        lots: 1,
        lotSize: SYMBOL_LOT_SIZES['NIFTY'] || 25, 
        targetPercent: 20,
        slPercent: -10
    };

    const [symbol, setSymbol] = useState('NIFTY');
    const [strategy, setStrategy] = useState('long-call');
    const [form, setForm] = useState(defaultFormState);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [analysis, setAnalysis] = useState("");
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [theme, setTheme] = useState('light');
    const [showTradePanel, setShowTradePanel] = useState(false);

    // --- PHASE 4 STATES ---
    const [isLiveMode, setIsLiveMode] = useState(false);
    const [liveData, setLiveData] = useState(null);
    const [selectedExpiry, setSelectedExpiry] = useState('');
    const [liveDataLoading, setLiveDataLoading] = useState(false);
    const [liveDataError, setLiveDataError] = useState(null);
    const [decisionResult, setDecisionResult] = useState(null); 
    const [signalStrength, setSignalStrength] = useState({ direction: 'BULL', strength: 'MODERATE' });
    const [aiSignal, setAiSignal] = useState(null);
    const [candleData, setCandleData] = useState([]);
    const [activeTrades, setActiveTrades] = useState({});

    // --- THEME & WEBSOCKET ---
    useEffect(() => {
        document.documentElement.classList.toggle('dark', theme === 'dark');
    }, [theme]);
    const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');

    // ✅ NEW: Auto-refresh when user picks a different expiry
    useEffect(() => {
        if (isLiveMode) {
            fetchLiveData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedExpiry]);

    useEffect(() => {
        if (isLiveMode && liveData) {
            const symbolKey = liveData.symbol || symbol;
            const atmStrike = findAtmStrike(liveData.spot, symbolKey);
            
            let newForm = autoFillPrimaryStrikes(form, strategy, atmStrike, symbolKey);
            
            if (liveData.options && liveData.options.length > 0) {
                newForm = updateAllPremiums(newForm, strategy, liveData);
            }

            // Always update lot size if backend sends it, otherwise keep existing
            if (liveData.lotSize) {
                newForm.lotSize = liveData.lotSize;
            }

            setForm(newForm);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveData, strategy, isLiveMode]);

    useEffect(() => {
        const ws = new WebSocket(WS_URL);
        ws.onopen = () => console.log('Connected to UI Stream');
        ws.onmessage = (event) => {
            const msg = JSON.parse(event.data);

            if (msg.type === 'HISTORY') setCandleData(msg.data);
            
            if (msg.type === 'CANDLE_CLOSE' || msg.type === 'TICK') {
                // ... (Keep your existing candle logic here) ...
            }
            if (msg.type === 'STATUS') {
            console.log("System Alert:", msg.message);
            // Optional: You could add a 'toast' notification here
        }

            // NEW: Handle Multiple Trades P&L
            if (msg.type === 'PNL_UPDATE') {
                setActiveTrades(prev => ({
                    ...prev,
                    [msg.tradeId]: msg // Update specific trade by its ID
                }));
            }

            if (msg.type === 'TRADE_CLOSE') {
                // Optional: You could remove it from the list here if you parsed the ID
                alert(msg.message); 
            }
        };
        return () => ws.close();
    }, []);

    const updateAllPremiums = useCallback((currentForm, currentStrategy, currentSymbolData) => {
        if (!currentSymbolData || !currentSymbolData.options) return currentForm;
        
        const newForm = { ...currentForm };
        
        const findPrice = (strike, type) => {
            if (!strike) return '';
            const opt = currentSymbolData.options.find(o => o.strike === Number(strike));
            if (!opt) return '';
            if (type === 'CE') return opt.CE?.ltp || 0;
            if (type === 'PE') return opt.PE?.ltp || 0;
            return 0;
        };

        if (currentStrategy.includes('protective') && currentSymbolData.spot) {
            newForm.stockPrice = currentSymbolData.spot;
        }

        switch (currentStrategy) {
            case 'long-call': case 'short-call': newForm.premium = findPrice(newForm.strike, 'CE'); break;
            case 'long-put': case 'short-put': newForm.premium = findPrice(newForm.strike, 'PE'); break;
            case 'protective-put': newForm.premium = findPrice(newForm.strike, 'PE'); break;
            case 'protective-call': newForm.premium = findPrice(newForm.strike, 'CE'); break;
            case 'bull-call-spread': case 'bear-call-spread': case 'call-ratio-spread':
                newForm.premium1 = findPrice(newForm.strike1, 'CE'); 
                newForm.premium2 = findPrice(newForm.strike2, 'CE'); break;
            case 'bull-put-spread': case 'bear-put-spread':
                newForm.premium1 = findPrice(newForm.strike1, 'PE'); 
                newForm.premium2 = findPrice(newForm.strike2, 'PE'); break;
            case 'short-straddle': case 'long-straddle': 
            case 'synthetic-long-stock': case 'synthetic-short-stock':
                newForm.premium1 = findPrice(newForm.strike, 'CE'); 
                newForm.premium2 = findPrice(newForm.strike, 'PE'); 
                if (currentStrategy.includes('synthetic')) {
                    newForm.premium = findPrice(newForm.strike, 'CE'); 
                    newForm.premium2 = findPrice(newForm.strike, 'PE'); 
                }
                break;
            case 'short-strangle': case 'long-strangle':
                newForm.premium1 = findPrice(newForm.strike1, 'PE'); 
                newForm.premium2 = findPrice(newForm.strike2, 'CE'); break;
            case 'call-butterfly':
                newForm.premium1 = findPrice(newForm.strike1, 'CE');
                newForm.premium2 = findPrice(newForm.strike2, 'CE');
                newForm.premium3 = findPrice(newForm.strike3, 'CE'); break;
            case 'jade-lizard':
                newForm.premium1 = findPrice(newForm.strike1, 'PE');
                newForm.premium2 = findPrice(newForm.strike2, 'CE');
                newForm.premium3 = findPrice(newForm.strike3, 'CE');
                break;
            case 'iron-condor':
                newForm.premium1 = findPrice(newForm.strike1, 'PE'); 
                newForm.premium2 = findPrice(newForm.strike2, 'PE'); 
                newForm.premium3 = findPrice(newForm.strike3, 'CE'); 
                newForm.premium4 = findPrice(newForm.strike4, 'CE'); break;
            case 'iron-butterfly':
                newForm.premium1 = findPrice(newForm.strike1, 'PE'); 
                newForm.premium2 = findPrice(newForm.strike2, 'PE'); 
                newForm.premium3 = findPrice(newForm.strike2, 'CE'); 
                newForm.premium4 = findPrice(newForm.strike3, 'CE'); break;
            default: break;
        }
        return newForm;
    }, []);

    // ✅ UPDATED: Now sends the 'selectedExpiry' to the backend
    const fetchLiveData = async () => {
        setLiveDataLoading(true); setLiveDataError(null);
        try {
            // Construct URL with optional expiry parameter
            let url = `${BACKEND_URL}/api/live-data-with-greeks/${encodeURIComponent(symbol)}`;
            if (selectedExpiry) {
                url += `?expiry=${selectedExpiry}`;
            }

            const res = await axios.get(url);
            const liveApiData = res.data;
            setLiveData(liveApiData);

            const symbolKey = liveApiData.symbol; 
            const atmStrike = findAtmStrike(liveApiData.spot, symbolKey);
            let newForm = autoFillPrimaryStrikes(form, strategy, atmStrike, symbolKey);
            
            if (liveApiData.lotSize) {
                newForm.lotSize = liveApiData.lotSize;
            }

            if (liveApiData.options && liveApiData.options.length > 0) {
                newForm = updateAllPremiums(newForm, strategy, liveApiData);
            }
            setForm(newForm);
        } catch (err) {
            setLiveDataError('Failed to fetch live Greeks data.');
            setIsLiveMode(false);
        } finally {
            setLiveDataLoading(false);
        }
    };

    const toggleLiveMode = () => {
        if (!isLiveMode) {
            setIsLiveMode(true);
            fetchLiveData(); 
        } else {
            setIsLiveMode(false);
            setLiveData(null);
        }
    };

    //const handleAutoDeploy = async () => {
    //    if (!liveData) { alert("Please start Live Greeks mode first."); return; }
    //    try {
    //        // 1. Get Recommendations (List of Strategies)
    //        const res = await axios.post(`${BACKEND_URL}/api/decide-and-build-order`, { symbol, signal: signalStrength });
    //        setDecisionResult(res.data);
    //        
    //        // REMOVED: Immediate execution logic. 
    //        // We now let the user choose which strategy to run from the UI.
    //        if (res.data.recommendedStrategies && res.data.recommendedStrategies.length > 0) {
    //            // Optional: Scroll to results or show a toast
    //            console.log("Strategies received:", res.data.recommendedStrategies);
    //        } else {
    //            alert(`Engine Skipped: ${res.data.reason || 'No strategies found'}`);
    //        }
    //    } catch (e) {
    //        console.error(e);
    //        alert("Deployment engine failed. Check console.");
    //    }
    //};

    const handleAutoDeploy = async () => {

       if (!liveData) {
           alert("Please start Live Greeks mode first.");
           return;
       }
   
       try {
   
           // ==========================================
           // STEP 1: LOGIN TO FYERS
           // ==========================================
   
           console.log("🔐 Logging into Fyers...");
   
           const loginRes = await axios.post(
               `${BACKEND_URL}/api/fyers/login`
           );
   
           console.log("Fyers Login Response:", loginRes.data);
   
           // Check login result
           if (!loginRes.data?.success) {
               throw new Error(
                   loginRes.data?.error || "Fyers login failed"
               );
           }
   
           console.log("✅ Fyers Login Successful");
   
   
           // ==========================================
           // STEP 2: RUN AUTO ENGINE
           // ==========================================
   
           console.log("🤖 Running Auto Execute Engine...");
   
           const res = await axios.post(
               `${BACKEND_URL}/api/decide-and-build-order`,
               {
                   symbol,
                   signal: signalStrength
               }
           );
   
           console.log("Engine Response:", res.data);
   
           setDecisionResult(res.data);
   
   
           // ==========================================
           // STEP 3: SHOW RECOMMENDATIONS
           // ==========================================
   
           if (
               res.data.recommendedStrategies &&
               res.data.recommendedStrategies.length > 0
           ) {
   
               console.log(
                   "✅ Strategies received:",
                   res.data.recommendedStrategies
               );
   
           } else {
   
               alert(
                   `Engine Skipped: ${
                       res.data.reason || "No strategies found"
                   }`
               );
           }
   
       } catch (e) {
   
           console.error("❌ Auto Execute Engine Error:", e);
   
           const errorMessage =
               e.response?.data?.details ||
               e.response?.data?.error ||
               e.message ||
               "Unknown error";
   
           alert(
               "❌ Auto Execute Engine Failed\n\n" +
               errorMessage
           );
       }
   };
    const handleExecuteSpecificStrategy = async (strategyObj) => {
        // FIX: Added 'window.' before confirm to satisfy ESLint
        if (!window.confirm(`Execute ${strategyObj.name} now?`)) return;

        try {
            const payload = { 
                strategy: strategyObj.name, 
                decisionData: { 
                    legs: strategyObj.legs,
                    symbol: symbol 
                } 
            };

            const execRes = await axios.post(`${BACKEND_URL}/api/execute-trade`, payload);
            
            if(execRes.data.success) {
                // FIX: Used window.alert just to be safe, though alert is sometimes lenient
                window.alert(`✅ Trade Executed: ${strategyObj.name}\nIDs: ${execRes.data.positions.map(p => p.orderId).join(", ")}`);
            }
        } catch (e) {
            console.error(e);
            window.alert("Execution failed: " + (e.response?.data?.error || e.message));
        }
    };

    
    
    const fetchAiSignal = async (selectedSymbol) => {
        try {
            console.log("Selected symbol:", selectedSymbol);
    
            const symbolMap = {
                NIFTY: "NSE:NIFTY50-INDEX",
                BANKNIFTY: "NSE:NIFTYBANK-INDEX",
                FINNIFTY: "NSE:FINNIFTY-INDEX",
                MIDCPNIFTY: "NSE:MIDCPNIFTY-INDEX",
                SENSEX: "BSE:SENSEX-INDEX",
    
                "NIFTY 50": "NSE:NIFTY50-INDEX",
                "NIFTY BANK": "NSE:NIFTYBANK-INDEX",
                "NIFTY FIN SERVICE": "NSE:FINNIFTY-INDEX",
                "NIFTY MIDCAP SELECT": "NSE:MIDCPNIFTY-INDEX",
            };
    
            const apiSymbol = symbolMap[selectedSymbol];
    
            if (!apiSymbol) {
                setAiSignal({
                    signal: "Not initiated",
                    confidence: "Symbol not mapped",
                });
                return;
            }
    
            const res = await axios.post("/model-api/generate-model-data", {
                symbol: apiSymbol,
                interval: "5",
            });
    
            setAiSignal(res.data);
        } catch (err) {
            console.error("AI Signal Error:", err);
            setAiSignal({
                signal: "Error",
                confidence: "API failed",
            });
        }
    };

useEffect(() => {
    fetchAiSignal(symbol);
}, []);

    const handleSymbolChange = (e) => {
        let newSymbol = e.target.value;
        if(newSymbol.includes('&amp;')){
           newSymbol = newSymbol.replace('&amp;', '&');
        }
        setSymbol(newSymbol);
	fetchAiSignal(newSymbol);
        setSelectedExpiry(''); // 🆕 RESET EXPIRY TO DEFAULT
        
        const configKey = getSymbolKey(newSymbol);
        // FIX: If lot size isn't known, default to 1, don't set it to 0 or null
        const newLotSize = (configKey && SYMBOL_LOT_SIZES[configKey]) ? SYMBOL_LOT_SIZES[configKey] : 1;
        
        setForm({ ...defaultFormState, lotSize: newLotSize });
        setData(null); setError(null); setAnalysis(""); setIsLiveMode(false); setLiveData(null); setDecisionResult(null); setActiveTrades({});
    };

    const handleStrategyChange = (e) => {
        const newStrategy = e.target.value;
        setStrategy(newStrategy);
        setData(null); setError(null); setAnalysis("");

        if (isLiveMode && liveData) {
            const symbolKey = liveData.symbol || symbol;
            const atmStrike = findAtmStrike(liveData.spot, symbolKey);
            let newForm = autoFillPrimaryStrikes(defaultFormState, newStrategy, atmStrike, symbolKey);
            
            if (liveData.lotSize) {
                newForm.lotSize = liveData.lotSize;
            }

            if (liveData.options.length > 0) newForm = updateAllPremiums(newForm, newStrategy, liveData);
            setForm(newForm);
        } else {
            setForm(prevForm => ({ ...defaultFormState, lotSize: prevForm.lotSize }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newForm = { ...form, [name]: value ? Number(value) : '' };
        if (isLiveMode && liveData && name.startsWith('strike')) {
            newForm = updateAllPremiums(newForm, strategy, liveData);
        }
        setForm(newForm);
    };

    const handleReset = () => {
        setForm(defaultFormState);
        setData(null); setError(null); setAnalysis(""); setDecisionResult(null); setActiveTrades({});
    };

    const handleSubmit = async () => {
        setError(null); setAnalysis(""); setIsLoading(true);
        try {
            const payload = { strategy, ...form, symbol: symbol };
            if (!payload.lotSize) payload.lotSize = 1;
            const res = await axios.post(`${BACKEND_URL}/calculate`, payload);
            setData(res.data);
        } catch (err) {
            setData(null);
            setError(err.response ? err.response.data.error : "An error occurred.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnalysis = async () => {
        if (!data) return;
        setIsAnalyzing(true); setAnalysis("");
        const strategyName = strategyConfigs[strategy].name;
        const prompt = `Analyze this options strategy: ${strategyName}. Params: ${JSON.stringify(form)}. Max Profit: ${data.maxProfit}, Max Loss: ${data.maxLoss}.`;
        try {
            const payload = { contents: [{ parts: [{ text: prompt }] }] };
            const response = await fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const result = await response.json();
            setAnalysis(result.candidates?.[0]?.content?.parts?.[0]?.text || "No analysis.");
        } catch (error) { setAnalysis(`Error: ${error.message}`); } finally { setIsAnalyzing(false); }
    };

    const handleSimulateTrade = async () => {
        if (!data) return;
        const symbolKey = liveData?.symbol || getSymbolKey(symbol) || symbol;
        const tradePayload = translateFormToTrade(strategy, form, symbolKey);
        if (!tradePayload) return;
        try {
            await axios.post(`${BACKEND_URL}/api/paper-trade`, tradePayload);
            setShowTradePanel(true);
        } catch (err) { setError("Error submitting paper trade."); }
    };

    const formatValue = (value) => (typeof value === 'number') ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value;

    const renderGreeksTable = () => {
        // 1. ADD THIS BLOCK: Handle loading state
        if (liveDataLoading) {
            return (
                <div className="mt-4 p-8 bg-white dark:bg-gray-800 rounded shadow text-center">
                    <span className="text-indigo-600 font-bold animate-pulse">
                        ⟳ Fetching Live Option Chain & Greeks...
                    </span>
                </div>
            );
        }
        if (!liveData || !liveData.options) return null;
        const atmIndex = liveData.options.reduce((closestIdx, opt, idx, arr) => Math.abs(opt.strike - liveData.spot) < Math.abs(arr[closestIdx].strike - liveData.spot) ? idx : closestIdx, 0);
        const subset = liveData.options.slice(Math.max(0, atmIndex - 6), Math.min(liveData.options.length, atmIndex + 7));

        return (
            <div className="mt-4 overflow-x-auto bg-white dark:bg-gray-800 p-4 rounded shadow">
                <h3 className="font-bold text-gray-700 dark:text-gray-200 mb-2">Live Greeks Chain (Spot: {liveData.spot}, VIX: {liveData.vix})</h3>
                <table className="min-w-full text-xs text-center border dark:border-gray-600">
                    <thead className="bg-gray-200 dark:bg-gray-700">
                        <tr>
                            <th className="p-2">CE Delta</th><th className="p-2">CE Gamma</th><th className="p-2">CE Theta</th>
                            <th className="p-2 bg-yellow-100 dark:bg-yellow-900 font-bold">Strike</th>
                            <th className="p-2">PE Delta</th><th className="p-2">PE Gamma</th><th className="p-2">PE Theta</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subset.map(row => (
                            <tr key={row.strike} className={row.strike === liveData.options[atmIndex].strike ? "bg-blue-50 dark:bg-blue-900/30 font-bold" : "border-b dark:border-gray-700"}>
                                <td className="p-2 text-green-600">{row.CE?.delta?.toFixed(2) || '-'}</td>
                                <td className="p-2">{row.CE?.gamma?.toFixed(4) || '-'}</td>
                                <td className="p-2 text-red-500">{row.CE?.theta?.toFixed(1) || '-'}</td>
                                <td className="p-2 font-bold">{row.strike}</td>
                                <td className="p-2 text-red-600">{row.PE?.delta?.toFixed(2) || '-'}</td>
                                <td className="p-2">{row.PE?.gamma?.toFixed(4) || '-'}</td>
                                <td className="p-2 text-red-500">{row.PE?.theta?.toFixed(1) || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto font-sans bg-gray-50 dark:bg-gray-900 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Algo Strategy Visualizer</h1>
                <button onClick={toggleTheme} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-2xl">{theme === 'light' ? '🌙' : '☀️'}</button>
            </div>

            <AlgoDashboard />

            <div className="p-6 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg mb-8 shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                            <div>
                                <h2 className="text-xl font-bold text-indigo-800 dark:text-indigo-300">Auto-Execute Engine</h2>
                                <p className="text-sm text-indigo-600 dark:text-indigo-400">Days To Expiry: {liveData?.daysToExpiry !== undefined ? liveData.daysToExpiry : 'N/A'}</p>
                            </div>
                        </div>

                        {/* 🆕 EXPIRY SELECTOR UI */}
                        <div className="mb-4 bg-white dark:bg-gray-800 p-3 rounded border dark:border-gray-700 flex justify-between items-center shadow-sm">
                            <div>
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Current Expiry</span>
                                <div className="font-mono text-lg font-bold text-indigo-600 dark:text-indigo-400">
                                    {liveData ? liveData.expiryDate : "Loading..."}
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <label className="text-xs font-bold text-gray-600 dark:text-gray-400">Change Expiry:</label>
                                <select 
                                    className="p-2 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600 text-sm"
                                    value={selectedExpiry}
                                    onChange={(e) => setSelectedExpiry(e.target.value)}
                                    disabled={!isLiveMode || !liveData}
                                >
                                    <option value="">Default (Nearest)</option>
                                    {/* Populate list from Backend Data */}
                                    {liveData && liveData.allExpiries && liveData.allExpiries.map((exp) => (
                                        <option key={exp.expiry} value={exp.expiry}>
                                            {exp.date} {/* Shows "28-Jan-2026" */}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

<div className="grid grid-cols-2 gap-2">
    <div>
        <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1">Select Instrument</label>
        <select className="w-full p-2 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600" value={symbol} onChange={handleSymbolChange} disabled={isLiveMode}>
            {Object.entries(SYMBOL_LIST).map(([cat, items]) => (
                <optgroup label={cat} key={cat}>
                    {items.map(s => <option key={s.symbol} value={s.symbol}>{s.name.split('(')[0].trim()}</option>)}
                </optgroup>
            ))}
        </select>
    </div>

    <div>
        <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1">Manual Signal</label>
        <select className="w-full p-2 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600" onChange={(e) => setSignalStrength({...signalStrength, direction: e.target.value})}>
            <option value="BULL">Signal: BULL</option>
            <option value="BEAR">Signal: BEAR</option>
            <option value="NEUTRAL">Signal: NEUTRAL</option>
        </select>
    </div>

    <div className="col-span-2 bg-white dark:bg-gray-800 p-2 rounded border dark:border-gray-700">
        <p className="text-xs font-bold text-gray-500">AI Signal</p>
        {aiSignal ? (
            <p className="text-sm font-bold">
                {aiSignal.signal} | Confidence: {aiSignal.confidence}
            </p>
        ) : (
            <p className="text-sm text-gray-400">No AI signal</p>
        )}
    </div>
</div>

                        <div className="flex gap-3">
                            <button onClick={toggleLiveMode} className={`flex-1 px-4 py-2 rounded text-white font-medium shadow ${isLiveMode ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}>
                                {isLiveMode ? 'Stop Feed' : 'Start Feed'}
                            </button>
                            <button onClick={handleAutoDeploy} disabled={!isLiveMode} className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded font-medium shadow hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                                🤖 Run Engine
                            </button>
                        </div>

                        <div className="space-y-2 mt-4">
                        {decisionResult && (
                            <div className="p-4 bg-white dark:bg-gray-800 rounded border border-l-4 border-indigo-500 shadow-sm text-sm">
                                <div className="flex justify-between items-center mb-3 border-b border-gray-100 dark:border-gray-700 pb-2">
                                    <span className="font-bold text-lg text-indigo-700 dark:text-indigo-300">
                                        Recommendations ({decisionResult.marketCondition})
                                    </span>
                                    <div className="text-right">
                                        <span className="text-xs text-gray-500 block">Score: {decisionResult.score || 0}</span>
                                        {liveData && liveData.daysToExpiry !== undefined && (
                                            <span className="text-xs font-semibold text-gray-500">
                                                Expiry: {liveData.expiryDate} ({liveData.daysToExpiry} Days)
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* --- NEW: List of Recommended Strategies --- */}
                                {decisionResult.recommendedStrategies && decisionResult.recommendedStrategies.length > 0 ? (
                                    <div className="grid grid-cols-1 gap-4">
                                        {decisionResult.recommendedStrategies.map((strat, index) => (
                                            <div key={index} className="border dark:border-gray-600 rounded p-3 bg-gray-50 dark:bg-gray-700/30 hover:shadow-md transition-shadow">
                                                <div className="flex justify-between items-center mb-2">
                                                    <h3 className="font-bold text-md text-gray-800 dark:text-gray-100">{strat.name}</h3>
                                                    <button
                                                        onClick={() => handleExecuteSpecificStrategy(strat)}
                                                        className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded shadow-sm font-medium"
                                                    >
                                                        ⚡ Execute
                                                    </button>
                                                </div>
                                                {/* LEGS DISPLAY */}
                                                <div className="space-y-1">
                                                    {strat.legs.map((leg, legIdx) => (
                                                        <div key={legIdx} className="flex justify-between items-center text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded border dark:border-gray-600">
                                                            <span className={`font-bold w-10 ${leg.action === 'BUY' ? 'text-green-600' : 'text-red-500'}`}>
                                                                {leg.action}
                                                            </span>
                                                            <span className="font-mono text-gray-700 dark:text-gray-300 flex-1 text-center">
                                                                {leg.strike} {leg.type || leg.optionType}
                                                            </span>
                                                            <span className="text-gray-400 w-16 text-right">
                                                                ₹{leg.price || leg.greeks?.ltp || '?'}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-red-500 italic text-xs">
                                        {decisionResult.reason || "No suitable strategies found for current market conditions."}
                                    </p>
                                )}

                                {decisionResult.expectedMove && (
                                    <div className="mt-3 text-xs text-gray-500 flex justify-between border-t dark:border-gray-700 pt-2">
                                        <span>Spot: {decisionResult.spot}</span>
                                        <span>Expected Move: ±{decisionResult.expectedMove}</span>
                                    </div>
                                )}
                            </div>
                        )}
                            {Object.keys(activeTrades).length > 0 && (
                                <div className="mt-4 mb-4">
                                    <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-2">
                                        🔴 Live Positions ({Object.keys(activeTrades).length})
                                    </h2>
                                    <div className="bg-white dark:bg-gray-800 rounded shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                                        <table className="min-w-full text-xs text-left">
                                            <thead className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold uppercase">
                                                <tr>
                                                    <th className="px-3 py-2">Symbol</th>
                                                    <th className="px-3 py-2">Strategy</th>
                                                    <th className="px-3 py-2">LTP</th>
                                                    <th className="px-3 py-2 text-right">P&L</th>
                                                    <th className="px-3 py-2 text-right">%</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                                {Object.values(activeTrades).map((trade) => (
                                                    <tr key={trade.tradeId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                                                        <td className="px-3 py-2 font-bold text-gray-800 dark:text-gray-100">
                                                            {trade.symbol}
                                                        </td>
                                                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                                            {trade.strategy || 'Custom'}
                                                        </td>
                                                        <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-300">
                                                            {trade.ltp?.toFixed(2)}
                                                        </td>
                                                        <td className={`px-3 py-2 font-bold text-right ${trade.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                            {trade.pnl >= 0 ? '+' : ''}{trade.pnl?.toFixed(2)}
                                                        </td>
                                                        <td className={`px-3 py-2 font-mono text-right ${trade.pnlPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                            {trade.pnlPercent?.toFixed(2)}%
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-gray-50 dark:bg-gray-900 font-bold text-gray-800 dark:text-white border-t dark:border-gray-600">
                                                <tr>
                                                    <td colSpan="3" className="px-3 py-2 text-right uppercase text-xs tracking-wider">Total P&L</td>
                                                    <td className={`px-3 py-2 text-right text-base ${
                                                        Object.values(activeTrades).reduce((sum, t) => sum + (t.pnl || 0), 0) >= 0 
                                                        ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        ₹{Object.values(activeTrades).reduce((sum, t) => sum + (t.pnl || 0), 0).toFixed(2)}
                                                    </td>
                                                    <td className="px-3 py-2"></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                         <LiveCandleChart data={candleData} />
                    </div>
                </div>
            </div>

            {isLiveMode && renderGreeksTable()}
            {liveDataError && <p className="text-red-500 text-sm mt-2 text-center">{liveDataError}</p>}

            <div className="mt-8 p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md mb-8">
                <div className="flex justify-between items-center mb-4 border-b dark:border-gray-600 pb-2">
                    <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200">Manual Strategy Builder</h2>
                    <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-gray-600 dark:text-gray-300">Live Mode: {isLiveMode ? 'ON' : 'OFF'}</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 items-end">
                    <div className="col-span-2 md:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Symbol</label>
                        <select value={symbol} onChange={handleSymbolChange} disabled={isLiveMode} className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white">
                             {Object.entries(SYMBOL_LIST).map(([cat, items]) => (
                                <optgroup label={cat} key={cat}>
                                    {items.map(s => <option key={s.symbol} value={s.symbol}>{s.name}</option>)}
                                </optgroup>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
                        <select value={strategy} onChange={handleStrategyChange} className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white">
                            {strategyGroups.map(group => ( <optgroup key={group.label} label={group.label}>{group.options.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}</optgroup> ))}
                        </select>
                    </div>
                    
                    {/* --- FIXED: REMOVED "hasValidConfig" check so STOCKS always show inputs --- */}
                    {strategy && strategyConfigs[strategy]?.fields.map(field => (
                         <div key={field} className="col-span-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{formatLabel(field, strategy)}</label>
                            <input type="number" name={field} value={form[field] || ''} onChange={handleChange} 
                                disabled={(isLiveMode && field.startsWith('premium'))}
                                className={`w-full p-2 border rounded dark:bg-gray-700 dark:text-white ${isLiveMode && field.startsWith('premium') ? 'bg-gray-100 dark:bg-gray-600' : ''}`} placeholder="0" />
                        </div>
                    ))}

                    <div className="col-span-2 flex space-x-2">
                        <button onClick={handleSubmit} disabled={isLoading} className={`w-full px-4 py-2 rounded text-white font-semibold ${data ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                            {isLoading ? '...' : (data ? 'Recalculate' : 'Calculate')}
                        </button>
                        <button onClick={handleReset} className="w-full bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">Reset</button>
                    </div>
                </div>
            </div>

            {error && <div className="p-4 bg-red-100 text-red-700 border border-red-400 rounded-lg text-center mb-8">{error}</div>}

            {data && (
                <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-xl mb-8">
                    <div className="flex justify-between items-center mb-6 border-b pb-2">
                        <h2 className="text-xl font-semibold dark:text-gray-200">Results</h2>
                        <div className="flex space-x-2">
                            <button onClick={handleSimulateTrade} className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">📈 Simulate Trade</button>
                            <button onClick={handleAnalysis} disabled={isAnalyzing} className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700">{isAnalyzing ? '...' : '✨ Analyze'}</button>
                        </div>
                    </div>
                    
                    {analysis && <div className="mb-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded prose dark:prose-invert" dangerouslySetInnerHTML={{ __html: analysis.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-center">
                        <div className="p-3 bg-green-100 rounded"><p className="text-sm font-semibold text-green-800">Max Profit</p><p className="text-xl font-bold text-green-900">{formatValue(data.maxProfit)}</p></div>
                        <div className="p-3 bg-red-100 rounded"><p className="text-sm font-semibold text-red-800">Max Loss</p><p className="text-xl font-bold text-red-900">{formatValue(data.maxLoss)}</p></div>
                        <div className="p-3 bg-yellow-100 rounded"><p className="text-sm font-semibold text-yellow-800">Breakeven</p><p className="text-xl font-bold text-yellow-900">{data.breakeven}</p></div>
                        <div className="p-3 bg-blue-100 rounded"><p className="text-sm font-semibold text-blue-800">Total Lots</p><p className="text-xl font-bold text-blue-900">{form.lots || 0}</p></div>
                    </div>

                    {data.payoffCurve && (
                        <div className="h-96 w-full">
                            <ResponsiveContainer>
                                <LineChart data={data.payoffCurve}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="spot" tick={{ fill: '#888' }} />
                                    <YAxis tick={{ fill: '#888' }} />
                                    <Tooltip contentStyle={{ backgroundColor: theme === 'dark' ? '#1f2937' : '#fff' }} />
                                    <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                                    {isLiveMode && liveData && <ReferenceLine x={liveData.spot} stroke="#007bff" label="Spot" />}
                                    <Line type="monotone" dataKey="payoff" stroke="#8884d8" dot={false} strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            )}

            {showTradePanel && <TradePanel />}
        </div>
    );
}

export default App;
